import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import useApi from "../../../CustomHooks/useApi";
import DynamicTable from "../../../ResuableCompoment/DynamicTable1";
import { getDisplayDate } from "../../../ResuableCompoment/GetDisplayDate";

/* ================= HELPERS ================= */
const getToday = () => new Date().toISOString().split("T")[0];

const FILTER_BUTTONS = [
    { label: "ACCOUNT STATEMENT", value: "ACCOUNT" },
    { label: "DISTRIBUTOR VALUE RECEIVED", value: "DISTRIBUTOR_SELF_VALUE_RECEIVED" },
    { label: "DISTRIBUTOR VALUE TRANSFER", value: "DISTRIBUTOR_SELF_BUSINESS_CASH_OUT" },
    { label: "DISTRIBUTOR FUND REQUEST", value: "FUND_REQUEST" },
    { label: "DISTRIBUTOR QR RECEIVED", value: "QR" },
    { label: "WALLET TO WALLET", value: "WALLET_TO_WALLET" },
    { label: "OVER DRAFT (OD)", value: "OD" },
    // { label: "DMT", value: "DMT" },
    // { label: "AEPS", value: "AEPS" },
    // { label: "PPI", value: "PPI" },
    // { label: "SETTLEMENT", value: "SETTLEMENT" },
    // { label: "RECHARGE", value: "RECHARGE" },
    // { label: "RETALIER VALUE IN", value: "BZCIN" },
    // { label: "BENE VERIFICATION", value: "BENE" },
    // { label: "RETAILER FUND REQUEST", value: "FUND_REQUEST" },
];

/* ================= COLUMNS ================= */
const columns = [
    {
        key: "txnId",
        label: "Txn ID",
    },
    {
        key: "txnDate",
        label: "Date",
        render: (v) => getDisplayDate(v),
    },
    {
        key: "narration",
        label: "Narration",
        render: (v) => (
            <span className="text-indigo-700 font-medium">
                {v || "—"}
            </span>
        ),
    },
    /* ================= AMOUNT SPLIT ================= */

    {
        key: "amount_cr",
        label: "Amount (CR)",
        render: (_, row) => {
            if (row.txnType === "REJECTED") {
                return (
                    <span className="font-semibold text-red-600">
                        ₹{Number(row.netAmount || 0).toFixed(2)}
                    </span>
                );
            }

            if (row.txnType === "CREDIT" || row.txnType === "APPROVED" || row.txnType === "REFUND") {
                return (
                    <span className="font-semibold text-green-700">
                        ₹{Number(row.netAmount || 0).toFixed(2)}
                    </span>
                );
            }

            return <span className="text-gray-400">—</span>;
        },
    },

    {
        key: "amount_dr",
        label: "Amount (DR)",
        render: (_, row) =>
            (row.txnType === "DEBIT" || row.txnType === "REVERSED") ? (
                <span className="font-semibold text-red-600">
                    ₹{Number(row.netAmount || 0).toFixed(2)}
                </span>
            ) : (
                <span className="text-gray-400">—</span>
            ),
    },

    /* ================= OTHER AMOUNTS (NO COLOR) ================= */

    {
        key: "charges",
        label: "Charge",
        render: (v) => (
            <span className="font-semibold text-red-700">
                ₹{Number(v || 0).toFixed(2)}
            </span>
        ),
    },
    {
        key: "tds",
        label: "TDS",
        render: (v) => (
            <span className="font-semibold text-red-800">
                ₹{Number(v || 0).toFixed(2)}
            </span>
        ),
    },
    /* ================= NET AMOUNT (COLOR LOGIC) ================= */

    {
        key: "txnAmount",
        label: "Net Amount",
        render: (v, row) => {
            const value = Number(v || 0);
            if (!row || !value) return <span className="text-gray-400">—</span>;

            if (row.txnType === "CREDIT" || row.txnType === "APPROVED" || row.txnType === "REJECTED" || row.txnType === "REFUND") {
                return (
                    <span className="font-semibold text-green-700">
                        ₹{value.toFixed(2)}
                    </span>
                );
            }

            if (row.txnType === "DEBIT" || row.txnType === "REVERSED") {
                return (
                    <span className="font-semibold text-red-600">
                        ₹{value.toFixed(2)}
                    </span>
                );
            }

            return <span className="text-gray-400">—</span>;
        },
    },

    {
        key: "commission",
        label: "Comm",
        render: (_, row) => {
            const value = Number(row.cashback || row.commission || 0);

            return (
                <span className="font-bold text-green-800">
                    ₹{value.toFixed(2)}
                </span>
            )
        },
    },
    {
        key: "balance",
        label: "Balance",
        render: (v) => (
            <span className="font-semibold text-blue-900">
                ₹{Number(v || 0).toFixed(2)}
            </span>
        ),
    },
];
const SummaryCard = ({
    title,
    value,
    isCurrency = true,
}) => {
    const formattedValue =
        typeof value === "number"
            ? value.toLocaleString("en-IN", {
                minimumFractionDigits: isCurrency ? 2 : 0,
                maximumFractionDigits: isCurrency ? 2 : 0,
            })
            : value;

    return (
        <div
            className="
        p-6 rounded-2xl
        bg-[#0A2A4F]                 /* dark blue */
        border border-blue-400/30
        shadow-lg
        transition-all duration-300
        hover:scale-[1.03]
        hover:shadow-[0_0_22px_rgba(59,130,246,0.35)]
        flex flex-col justify-between
      "
        >
            {/* TITLE */}
            <div className="text-white">
                {title}
            </div>

            {/* VALUE */}
            <div className="mt-4">
                <p className="text-white font-extrabold text-2xl sm:text-3xl leading-tight break-words">
                    {isCurrency ? "₹" : ""}
                    {formattedValue}
                </p>
            </div>
        </div>
    );
};

const DistributorReports1 = () => {
    const mid = useSelector(state => state.auth.employeeDetails?.username);

    const { callApi: ledgerreport } = useApi(
        "/ledger/retailer",
        "GET"
    );

    /* ================= STATES ================= */
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [goToPage, setGoToPage] = useState("");
    const [filterType, setFilterType] = useState("ACCOUNT");

    const [isExporting, setIsExporting] = useState(false);


    // 🔥 NEW STATE: Tracks if a search has been completed
    const [isSearched, setIsSearched] = useState(false);

    // If the user changes any filters, hide the export button until they search again
    useEffect(() => {
        setIsSearched(false);
    }, [fromDate, toDate, filterType]);


    /* ================= FETCH LEDGER ================= */
    const fetchLedger = async (
        targetPage = 0,
        loadMore = false,
        fDate = fromDate,
        tDate = toDate,
        fType = filterType
    ) => {
        setLoading(true);

        let query = `?bid=${mid}&page=${targetPage}&size=${size}&scope=self`;
        if (fDate) query += `&fromDate=${fDate}`;
        if (tDate) query += `&toDate=${tDate}`;

        // 🔥 IMPORTANT LOGIC
        if (fType && fType !== "ACCOUNT") {
            query += `&feature=${fType}`;
        }

        try {
            const res = await ledgerreport(null, query);
            const data = (res?.content || []).filter(Boolean);

            setTotalPages(res?.totalPages || 0);
            setTotalElements(res?.totalElements || 0);

            setHistory(prev =>
                loadMore ? [...prev, ...data] : data
            );

            setPage(targetPage);
            setIsSearched(true);
        } catch (err) {
            console.error(err);
            if (!loadMore) setHistory([]);
        } finally {
            setLoading(false);
        }
    };




    /* ================= INITIAL LOAD (TODAY) ================= */
    useEffect(() => {
        const today = getToday();
        if (today) {
            setFromDate(today);
            setToDate(today);
            fetchLedger(0, false, today, today, "ACCOUNT");

        }

        // eslint-disable-next-line
    }, []);

    /* ================= PAGE SIZE CHANGE ================= */
    useEffect(() => {
        if (fromDate && toDate) {
            fetchLedger(0, false, fromDate, toDate);
        }
        // eslint-disable-next-line
    }, [size]);

    /* ================= SEARCH ================= */
    const handleSearch = () => {
        fetchLedger(0, false, fromDate, toDate, filterType);
    };


    const handleFilterClick = (type) => {
        setFilterType(type);
        setHistory([]);
        setPage(0);
        fetchLedger(0, false, fromDate, toDate, type);
    };


    /* ================= GO TO PAGE ================= */
    // const handleGoToPage = () => {
    //     const p = Number(goToPage);
    //     if (p < 1 || p > totalPages) {
    //         alert(`Enter page between 1 and ${totalPages}`);
    //         return;
    //     }
    //     fetchLedger(p - 1, false, fromDate, toDate);
    // };

    // /* ================= LOAD MORE ================= */
    // const handleLoadMore = () => {
    //     if (page + 1 < totalPages) {
    //         fetchLedger(page + 1, true, fromDate, toDate);
    //     }
    // };

    /* ================= PAGINATION ACTIONS ================= */
    const handleGoToPage = () => {
        const p = Number(goToPage);
        if (p < 1 || p > totalPages) {
            alert(`Enter page between 1 and ${totalPages}`);
            return;
        }
        fetchLedger(p - 1, false, fromDate, toDate); // false = replace data
    };

    // 🔥 1. Handle Prev/Next and Page Number clicks
    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            fetchLedger(newPage, false, fromDate, toDate); // false = replace data
            
            // Optional: Scroll to top of table when page changes
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // 🔥 2. Helper to generate page numbers with ellipses
    const getPaginationPages = (current, total) => {
        if (total <= 1) return [];
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        const pages = [];
        const delta = 2;
        let prev = null;
        for (let i = 1; i <= total; i++) {
            if (i === 1 || i === total || (i >= current + 1 - delta && i <= current + 1 + delta)) {
                if (prev !== null && i - prev > 1) pages.push("...");
                pages.push(i);
                prev = i;
            }
        }
        return pages;
    };

    /* ================= EXCEL DOWNLOAD (SIZE = 10000) ================= */
    // const downloadExcel = async () => {
    //     setLoading(true);

    //     let allData = [];
    //     let currentPage = 0;
    //     const excelSize = 1000; // keep safe for backend

    //     try {
    //         while (true) {
    //             let query = `?bid=${mid}&page=${currentPage}&size=${excelSize}&scope=self`;

    //             if (fromDate) query += `&fromDate=${fromDate}`;
    //             if (toDate) query += `&toDate=${toDate}`;

    //             // 🔥 SAME FILTER AS UI
    //             if (filterType && filterType !== "ACCOUNT") {
    //                 query += `&feature=${filterType}`;
    //             }

    //             const res = await ledgerreport(null, query);

    //             const pageData = (res?.content || []).filter(Boolean);

    //             allData = [...allData, ...pageData];

    //             if (currentPage + 1 >= res?.totalPages) break;
    //             currentPage++;
    //         }

    //         if (allData.length === 0) {
    //             alert("No data found for Excel");
    //             return;
    //         }

    //         /* ================= FORMAT ROWS ================= */
    //         const rows = allData.map(r => ({
    //             Date: getDisplayDate(r.txnDate),
    //             TxnID: r.txnId,
    //             Shop: r.shopName,
    //             Narration: r.narration,
    //             Type: r.txnType,
    //             Credit:
    //                 r.txnType === "CREDIT" || r.txnType === "APPROVED" || r.txnType === "REJECTED"
    //                     ? r.txnAmount
    //                     : "",
    //             Debit:
    //                 r.txnType === "DEBIT" || r.txnType === "REVERSED"
    //                     ? r.netAmount
    //                     : "",
    //             Refund:
    //                 r.txnType === "REFUND"
    //                     ? r.txnAmount
    //                     : "",
    //             Charge: r.charges,
    //             Cashback: r.cashback,
    //             TDS: r.tds,
    //             Commission: r.commission,
    //             Balance: r.balance,
    //         }));

    //         /* ================= XLSX ================= */
    //         const ws = XLSX.utils.json_to_sheet(rows);
    //         const wb = XLSX.utils.book_new();
    //         XLSX.utils.book_append_sheet(wb, ws, "Ledger");

    //         XLSX.writeFile(
    //             wb,
    //             `Ledger_${filterType}_${fromDate}_to_${toDate}.xlsx`
    //         );
    //     } catch (err) {
    //         console.error(err);
    //         alert("Excel download failed");
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    /* ================= EXCEL DOWNLOAD (DYNAMIC SIZE) ================= */
    // const downloadExcel = async () => {
    //     setIsExporting(true);

    //     try {
    //         // 1. Quick fetch to find the EXACT total elements for this filter & date
    //         let countQuery = `?bid=${mid}&page=0&size=1&scope=self`;
    //         if (fromDate) countQuery += `&fromDate=${fromDate}`;
    //         if (toDate) countQuery += `&toDate=${toDate}`;
    //         if (filterType && filterType !== "ACCOUNT") {
    //             countQuery += `&feature=${filterType}`;
    //         }

    //         const countRes = await ledgerreport(null, countQuery);
    //         const exactTotalElements = countRes?.totalElements || 0;

    //         if (exactTotalElements === 0) {
    //             alert("No data found for the selected filters.");
    //             setIsExporting(false);
    //             return;
    //         }

    //         // 2. Fetch everything at once using exactTotalElements dynamically
    //         let fullQuery = `?bid=${mid}&page=0&size=${exactTotalElements}&scope=self`;
    //         if (fromDate) fullQuery += `&fromDate=${fromDate}`;
    //         if (toDate) fullQuery += `&toDate=${toDate}`;
    //         if (filterType && filterType !== "ACCOUNT") {
    //             fullQuery += `&feature=${filterType}`;
    //         }

    //         const fullRes = await ledgerreport(null, fullQuery);
    //         const allData = (fullRes?.content || []).filter(Boolean);

    //         /* ================= FORMAT EXCEL ROWS ================= */
    //         const rows = allData.map(r => ({
    //             Date: getDisplayDate(r.txnDate),
    //             TxnID: r.txnId,
    //             Shop: r.shopName,
    //             Narration: r.narration,
    //             Type: r.txnType,
    //             Credit:
    //                 r.txnType === "CREDIT" || r.txnType === "APPROVED" || r.txnType === "REJECTED"
    //                     ? r.txnAmount
    //                     : "",
    //             Debit:
    //                 r.txnType === "DEBIT" || r.txnType === "REVERSED"
    //                     ? r.netAmount
    //                     : "",
    //             Refund:
    //                 r.txnType === "REFUND"
    //                     ? r.txnAmount
    //                     : "",
    //             Charge: r.charges,
    //             Cashback: r.cashback,
    //             TDS: r.tds,
    //             Commission: r.commission,
    //             Balance: r.balance,
    //         }));

    //         /* ================= XLSX ================= */
    //         const ws = XLSX.utils.json_to_sheet(rows);
    //         const wb = XLSX.utils.book_new();
    //         XLSX.utils.book_append_sheet(wb, ws, "Ledger");

    //         XLSX.writeFile(
    //             wb,
    //             `Ledger_${filterType}_${fromDate}_to_${toDate}.xlsx`
    //         );
    //     } catch (err) {
    //         console.error(err);
    //         alert("Excel download failed");
    //     } finally {
    //         setIsExporting(false);
    //     }
    // };



    /* ================= EXCEL DOWNLOAD (DYNAMIC SIZE) ================= */
    const downloadExcel = async () => {
        setIsExporting(true);

        try {
            if (totalElements === 0) {
                alert("No data available to export.");
                setIsExporting(false);
                return;
            }

            // 1. Fetch everything at once using totalElements dynamically directly from state
            let fullQuery = `?bid=${mid}&page=0&size=${totalElements}&scope=self`;
            if (fromDate) fullQuery += `&fromDate=${fromDate}`;
            if (toDate) fullQuery += `&toDate=${toDate}`;
            if (filterType && filterType !== "ACCOUNT") {
                fullQuery += `&feature=${filterType}`;
            }

            const fullRes = await ledgerreport(null, fullQuery);
            const allData = (fullRes?.content || []).filter(Boolean);

            /* ================= FORMAT EXCEL ROWS ================= */
            const rows = allData.map(r => ({
                Date: getDisplayDate(r.txnDate),
                TxnID: r.txnId,
                Shop: r.shopName,
                Narration: r.narration,
                Type: r.txnType,
                Credit:
                    r.txnType === "CREDIT" || r.txnType === "APPROVED" || r.txnType === "REJECTED"
                        ? r.txnAmount
                        : "",
                Debit:
                    r.txnType === "DEBIT" || r.txnType === "REVERSED"
                        ? r.netAmount
                        : "",
                Refund:
                    r.txnType === "REFUND"
                        ? r.txnAmount
                        : "",
                Charge: r.charges,
                Cashback: r.cashback,
                TDS: r.tds,
                Commission: r.commission,
                Balance: r.balance,
            }));

            /* ================= XLSX ================= */
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Ledger");

            XLSX.writeFile(
                wb,
                `Ledger_${filterType}_${fromDate}_to_${toDate}.xlsx`
            );
        } catch (err) {
            console.error(err);
            alert("Excel download failed");
        } finally {
            setIsExporting(false);
        }
    };



    /* ================= TOTAL CALCULATIONS ================= */
    const totalCR = history.reduce(
        (sum, tx) =>
            (tx?.txnType === "CREDIT" || tx?.txnType === "APPROVED" || tx.txnType === "REJECTED")
                ? sum + Number(tx?.netAmount || 0)
                : sum,
        0
    );

    const totalDR = history.reduce(
        (sum, tx) =>
            (tx?.txnType === "DEBIT" || tx.txnType === "REVERSED")
                ? sum + Number(tx?.netAmount || 0)
                : sum,
        0
    );

    const totalNetAmount = history.reduce(
        (sum, tx) => sum + Number(tx?.txnAmount || 0),
        0
    );


    const totalCharges = history.reduce(
        (sum, tx) => sum + Number(tx?.charges || 0),
        0
    );

    const totalTds = history.reduce(
        (sum, tx) => sum + Number(tx?.tds || 0),
        0
    );

    const totalCommission = history.reduce(
        (sum, tx) => sum + Number(tx?.commission || 0),
        0
    );


    return (
        <div className="min-h-screen p-6 bg-[#EEF1F5]">

            {/* ================= PAGE TITLE ================= */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-[#0A2A4F]">
                    Ledger Report
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                    Complete transaction history with detailed breakup
                </p>
            </div>

            {/* ================= SUMMARY CARDS ================= */}
            {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-10">

          
                <SummaryCard
                    title={
                        <>
                            <span className="text-lg font-semibold">Total Records</span>
                            <span className="block text-sm font-bold mt-1">
                                कुल रिकॉर्ड
                            </span>
                        </>
                    }
                    value={totalElements}
                    isCurrency={false}
                />

        
                <SummaryCard
                    title={
                        <>
                            <span className="text-lg font-semibold">Total Credit</span>
                            <span className="block text-sm font-bold mt-1">
                                जो आपके खाते में आया है
                            </span>
                        </>
                    }
                    value={totalCR}
                />

                
                <SummaryCard
                    title={
                        <>
                            <span className="text-lg font-semibold">Total Debit</span>
                            <span className="block text-sm font-bold mt-1">
                                जो ग्राहक के खाते में गया है
                            </span>
                        </>
                    }
                    value={totalDR}
                />

   
                <SummaryCard
                    title={
                        <>
                            <span className="text-lg font-semibold">Total Refund</span>
                            <span className="block text-sm font-bold mt-1">
                                जो वापस आपके खाते में आया है
                            </span>
                        </>
                    }
                    value={totalRR}
                />

       
                <SummaryCard
                    title={
                        <>
                            <span className="text-lg font-semibold">Net Amount</span>
                            <span className="block text-sm font-bold mt-1">
                                आपके खाते में आए हुए पैसे, आपके खाते से कटे हुए पैसे और लगाए गए चार्ज — इन सभी का कुल जोड़।
                            </span>
                        </>
                    }
                    value={totalNetAmount}
                />

            </div> */}





            {/* ================= INFO NOTE ================= */}
            {/* <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-5 text-sm text-slate-700">
                <p className="font-semibold text-blue-800 mb-2">
                    ℹ️ Total & Excel Calculation Info
                </p>

                <p className="leading-relaxed">
                    <b>English:</b> Totals are calculated only from currently loaded
                    records. Clicking <b>Load More</b> updates totals automatically.
                    Excel download always includes the full selected date range.
                </p>

                <p className="mt-3 leading-relaxed">
                    <b>हिंदी:</b> कुल राशि केवल लोड किए गए रिकॉर्ड से निकाली जाती है।
                    <b> Load More</b> पर नए रिकॉर्ड जुड़ते हैं और कुल अपने आप अपडेट होता है।
                    Excel में चुनी गई तारीख की पूरी रिपोर्ट आती है।
                </p>
            </div> */}

            {/* ================= FILTER BUTTONS ================= */}
            <div className="flex flex-wrap gap-3 mb-6">
                {FILTER_BUTTONS.map(btn => (
                    <button
                        key={btn.value}
                        onClick={() => handleFilterClick(btn.value)}
                        className={`
            px-6 py-3 rounded-xl border text-sm font-semibold
            transition-all duration-300
            ${filterType === btn.value
                                ? "bg-[#0A2A4F] text-white shadow-lg scale-105"
                                : "bg-white text-[#0A2A4F] border-gray-300 hover:bg-slate-100"}
          `}
                    >
                        {btn.label}
                    </button>
                ))}
            </div>

            {/* ================= FILTER BAR ================= */}
            <div className="bg-white rounded-2xl shadow-sm border p-6 mb-8">

                {/* ================= FILTER CONTROLS ================= */}
                <div className="flex flex-wrap items-end gap-6">

                    {/* ===== FROM DATE ===== */}
                    <div>
                        <label className="text-xs text-gray-500">From Date</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={e => setFromDate(e.target.value)}
                            className="h-11 w-44 rounded-lg border px-3 mt-1"
                        />
                    </div>

                    {/* ===== TO DATE ===== */}
                    <div>
                        <label className="text-xs text-gray-500">To Date</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={e => setToDate(e.target.value)}
                            className="h-11 w-44 rounded-lg border px-3 mt-1"
                        />
                    </div>

                    {/* ===== SEARCH BUTTON ===== */}
                    <button
                        onClick={handleSearch}
                        className="h-11 px-8 rounded-full bg-[#0A2A4F] text-white
                 font-semibold shadow-md hover:bg-[#08305F]"
                    >
                        Search
                    </button>

                    {/* ===== EXPORT ===== */}
                    {/* <button
                        onClick={downloadExcel}
                        disabled={loading}
                        className="h-11 px-8 rounded-full bg-green-700 text-white
               font-semibold shadow-md hover:bg-green-800
               disabled:opacity-50"
                    >
                        {loading ? "Preparing Excel..." : "Export Full Ledger"}
                    </button> */}
                    {/* ===== EXPORT ===== */}
                    {/* <button
                        onClick={downloadExcel}
                        disabled={loading || isExporting}
                        className="h-11 px-8 rounded-full bg-green-700 text-white
               font-semibold shadow-md hover:bg-green-800
               disabled:opacity-50 flex items-center justify-center"
                    >
                        {isExporting ? "Preparing Excel..." : "Export Full Ledger"}
                    </button> */}
                    {/* ===== EXPORT (ONLY VISIBLE IF SEARCHED & DATA EXISTS) ===== */}
                    {isSearched && totalElements > 0 && (
                        <button
                            onClick={downloadExcel}
                            disabled={loading || isExporting}
                            className="h-11 px-8 rounded-full bg-green-700 text-white
                   font-semibold shadow-md hover:bg-green-800
                   disabled:opacity-50 flex items-center justify-center"
                        >
                            {isExporting ? "Preparing Excel..." : "Export Full Ledger"}
                        </button>
                    )}

                </div>

                {/* ================= PAGINATION INFO ================= */}
                <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-gray-600">
                    <span><b>Total Records:</b> {totalElements}</span>
                    <span><b>Pages:</b> {totalPages}</span>
                    <span><b>Current Page:</b> {page + 1}</span>

                    <div className="flex items-center gap-2 ml-auto">
                        <input
                            type="number"
                            placeholder="Go to page"
                            value={goToPage}
                            onChange={e => setGoToPage(e.target.value)}
                            className="h-9 w-24 rounded-lg border px-2"
                        />
                        <button
                            onClick={handleGoToPage}
                            className="h-9 px-4 rounded-lg bg-indigo-600 text-white"
                        >
                            Go
                        </button>
                    </div>

                    <select
                        value={size}
                        onChange={e => setSize(Number(e.target.value))}
                        className="h-9 rounded-lg border px-2"
                    >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={150}>150</option>
                        <option value={500}>500</option>
                        <option value={1000}>1000</option>
                        <option value={2000}>2000</option>
                    </select>
                </div>
            </div>


            {/* ================= TABLE ================= */}
            <div>
                <DynamicTable
                    columns={columns}
                    data={history}
                    isLoading={loading}
                    emptyText="No ledger records found"
                    rowClassName={(row) => {
                        if (row.txnType === "DEBIT" || row.txnType === "REVERSED") {
                            return "bg-red-100"; // 🔴 Darker Debit
                        }

                        if (row.txnType === "REFUND") {
                            return "bg-yellow-100"; // 🟡 Darker Refund
                        }

                        if (
                            row.txnType === "CREDIT" ||
                            row.txnType === "APPROVED"
                        ) {
                            return "bg-green-100"; // 🟢 Darker Credit
                        }

                        if (row.txnType === "REJECTED") {
                            return "bg-red-200 border-l-4 border-red-600"; // 🔴 More strong rejected
                        }

                        return "";
                    }}
                    footerTotals={{
                        totalCR,
                        totalDR,
                        totalNetAmount,
                        totalCharges,
                        totalTds,
                        totalCommission,
                    }}
                />

                {/* ================= LOAD MORE ================= */}
                {/* {page + 1 < totalPages && (
                    <div className="flex justify-center mt-6">
                        <button
                            onClick={handleLoadMore}
                            disabled={loading}
                            className="px-8 py-3 rounded-xl bg-gray-900 text-white font-semibold hover:bg-gray-800"
                        >
                            {loading ? "Loading..." : "Load Next Transactions"}
                        </button>
                    </div>
                )} */}


                {/* ================= PAGINATION SYSTEM ================= */}
                {totalPages > 1 && (
                    <div className="flex flex-wrap items-center justify-center gap-2 pt-6 pb-2">
                        
                        {/* Previous Button */}
                        <button
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 0 || loading}
                            className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-600 font-semibold text-xs transition hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                            Previous
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center gap-1.5 hidden sm:flex">
                            {getPaginationPages(page, totalPages).map((p, i) =>
                                p === "..." ? (
                                    <span key={`ellipsis-${i}`} className="w-8 text-center text-slate-400 font-medium">...</span>
                                ) : (
                                    <button
                                        key={p}
                                        onClick={() => handlePageChange(p - 1)}
                                        disabled={loading}
                                        className={`h-10 min-w-[40px] px-2 rounded-xl border text-xs font-bold transition flex items-center justify-center
                                            ${page === p - 1
                                                ? "bg-slate-900 text-white border-slate-900 shadow-md"
                                                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                                            }`}
                                    >
                                        {p}
                                    </button>
                                )
                            )}
                        </div>

                        {/* Next Button */}
                        <button
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page + 1 >= totalPages || loading}
                            className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-600 font-semibold text-xs transition hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                            Next
                        </button>

                    </div>
                )}
                
            </div>
        </div>
    );

};

export default DistributorReports1;